<template>
  <div>
    <div class="field">
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="selectedOperationId" @change="operationSelected">
            <option v-for="operation in operations" :key="operation.id" :value="operation.id">
              {{ operation.title }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="field" v-if="requiresInput">
      <div class="control is-fullwidth">
        <input
          :value="liveCondition.value"
          type="text"
          class="input"
          @input="(e) => textChanged((e.target as HTMLInputElement).value)"
        />
        <p class="help is-danger" v-if="isRequiredInvalid">Value required</p>
        <p class="help is-danger" v-else-if="isDecimalInvalid">Must be a number with maximum of 2 decimal places</p>
        <p class="help is-danger" v-else-if="isMaxValueInvalid">Must be a maximum of 25 characters</p>
      </div>
    </div>

    <div class="control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Conditions, OperationPickerOption, ConditionOperationType } from './../models/conditions';
import { ConditionViewModel } from './../models/conditionViewModel';
import { required, maxLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { PropType, ref, onMounted, onBeforeUnmount, computed } from 'vue';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

const isDecimal = (value: string) => {
  const decimalRegex: RegExp = /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/;
  return decimalRegex.test(value);
};
const validations = computed(() => ({
  liveCondition: {
    value: {
      required: requiredIf(() => liveCondition.value.operation !== ConditionOperationType.IsSet),
      isDecimal,
      maxLength: maxLength(25),
    },
  },
}));

const $v = useVuelidate(validations, { liveCondition });
const isRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.liveCondition.value));
const isDecimalInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'isDecimal'),
);
const isMaxValueInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'maxLength'),
);

const operations: OperationPickerOption[] = Conditions.Operations.decimal.map((op, index) => {
  return {
    id: `${index}`,
    title: op.title,
    operation: op.operation,
    result: op.result,
  };
});

const selectedOperationId = ref('');

onMounted(() => {
  if (props.condition) {
    liveCondition.value = {
      attribute: props.condition.attribute,
      operation: props.condition.operation,
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: props.condition.unit,
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }

  selectedOperationId.value = getSelectedOperation();
  operationSelected();
});

onBeforeUnmount(() => makeFriendlyDecimal());

function getSelectedOperation() {
  if (liveCondition.value) {
    const selectedOperation = operations.find(
      (op) => op.operation === liveCondition.value.operation && op.result === liveCondition.value.result,
    );
    if (selectedOperation) {
      return selectedOperation.id;
    }
  }

  return operations[0].id;
}

function operationSelected() {
  const selectedOperation = operations.find((op) => op.id === selectedOperationId.value);
  if (selectedOperation) {
    liveCondition.value.operation = selectedOperation.operation;
    liveCondition.value.result = selectedOperation.result;
    emitConditionChanged();
  }
}

function textChanged(value: string) {
  liveCondition.value.value = value;
  $v.value.$touch();
  emitConditionChanged();
}

const requiresInput = computed(() => liveCondition.value.operation !== ConditionOperationType.IsSet);

function emitConditionChanged() {
  if (props.condition.isNew) {
    props.condition.isNew = false;
  } else {
    liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
    emit('changed', liveCondition.value);
  }
}

function complete() {
  makeFriendlyDecimal();
  liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
  emit('completed', liveCondition.value);
}

function makeFriendlyDecimal() {
  if (!$v.value.$invalid) {
    // if it's valid, append decimal places if the input doesn't contain a . or ends in a .
    liveCondition.value.value = padLeadingZeroAndDecimalPlaces(liveCondition.value.value);
  }
}

function padLeadingZeroAndDecimalPlaces(value: string) {
  const decimalPosition = value.indexOf('.');

  if (decimalPosition === -1) {
    return value + '.00';
  }

  if (decimalPosition === 0) {
    return '0' + value;
  }

  if (decimalPosition === value.length - 1) {
    return value + '00';
  }

  return value;
}
</script>
